<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { MESSAGE_STATUS, MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { detectLanguage } from 'dashboard/helper/languageDetector';

const {
  content,
  contentAttributes,
  status,
  sender,
  toEmails,
  ccEmails,
  bccEmails,
  id,
  conversationId,
  isBotOrAgentMessage,
  messageType,
} = useMessageContext();

const detectedLanguage = ref(null);
const isDetectingLanguage = ref(false);
const emit = defineEmits(['language-detected']);
const lastDetectedText = ref('');

const detectedLanguageLabel = computed(() => {
  return detectedLanguage.value?.label || '';
});

const hasError = computed(() => {
  return status.value === MESSAGE_STATUS.FAILED;
});

const normalizeEmailList = value => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map(emailAddress => emailAddress.trim())
      .filter(Boolean);
  }

  return [];
};

const isLikelyIncomingEmail = computed(() => {
  const type = messageType?.value;
  if (type === MESSAGE_TYPES.INCOMING) {
    return true;
  }

  const attributes = contentAttributes.value || {};
  const emailPayload = attributes.email || {};

  const direction = emailPayload.direction || emailPayload.direction_type;
  if (direction) {
    return String(direction).toLowerCase() !== 'outgoing';
  }

  const fromAddresses = normalizeEmailList(emailPayload.from ?? emailPayload.from_emails);
  const senderEmail = sender.value?.email;

  if (fromAddresses.length && senderEmail) {
    const normalizedSender = senderEmail.toLowerCase();
    const allMatchSender = fromAddresses.every(
      address => address.toLowerCase() === normalizedSender
    );

    if (!allMatchSender) {
      return true;
    }
  }

  if (isBotOrAgentMessage?.value === false) {
    return true;
  }

  if (type === MESSAGE_TYPES.OUTGOING) {
    return false;
  }

  return true;
});

const shouldDetectLanguage = computed(() => {
  return isLikelyIncomingEmail.value;
});

const fromEmail = computed(() => {
  const { email = {} } = contentAttributes.value || {};
  return normalizeEmailList(email.from ?? email.from_emails);
});

const toEmail = computed(() => {
  const attributes = contentAttributes.value || {};
  const emailPayload = attributes.email || {};
  const candidates = [
    emailPayload.to,
    emailPayload.to_emails,
    attributes.toEmails,
    attributes.to_emails,
    toEmails?.value,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeEmailList(candidate);
    if (normalized.length) {
      return normalized;
    }
  }

  return [];
});

const ccEmail = computed(() => {
  const attributes = contentAttributes.value || {};
  const emailPayload = attributes.email || {};
  const candidates = [
    emailPayload.cc,
    emailPayload.cc_emails,
    attributes.ccEmails,
    attributes.cc_emails,
    ccEmails?.value,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeEmailList(candidate);
    if (normalized.length) {
      return normalized;
    }
  }

  return [];
});

const cannedReplyMeta = computed(() => {
  const attributes = contentAttributes.value || {};
  return attributes.canned_reply || attributes.cannedReply || null;
});

const cannedReplyDisplay = computed(() => {
  if (!cannedReplyMeta.value) {
    return '';
  }

  const { name, shortCode, language, locale } = cannedReplyMeta.value;
  const templateName = name || shortCode || '';

  if (!templateName && !language && !locale) {
    return '';
  }

  const details = [];
  if (language) {
    details.push(language);
  }
  if (locale) {
    details.push(String(locale).toUpperCase());
  }

  if (!details.length) {
    return `/${templateName}`;
  }

  return `/${templateName} - ${details.join(' - ')}`;
});

const senderName = computed(() => {
  const fromEmailAddress = fromEmail.value[0] ?? '';
  const senderEmail = sender.value.email ?? '';

  if (!fromEmailAddress && !senderEmail) return null;

  // if the sender of the conversation and the sender of this particular
  // email are the same, only then we return the sender name
  if (fromEmailAddress === senderEmail) {
    return sender.value.name;
  }

  return null;
});

const bccEmail = computed(() => {
  const attributes = contentAttributes.value || {};
  const emailPayload = attributes.email || {};
  const candidates = [
    emailPayload.bcc,
    emailPayload.bcc_emails,
    attributes.bccEmails,
    attributes.bcc_emails,
    bccEmails?.value,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeEmailList(candidate);
    if (normalized.length) {
      return normalized;
    }
  }

  return [];
});

const subject = computed(() => {
  return contentAttributes.value?.email?.subject ?? '';
});

const showMeta = computed(() => {
  return (
    fromEmail.value[0] ||
    toEmail.value.length ||
    ccEmail.value.length ||
    bccEmail.value.length ||
    subject.value ||
    cannedReplyDisplay.value ||
    detectedLanguageLabel.value
  );
});

const sanitizeText = value => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.replace(/<[^>]*>/g, ' ');
};

const emailTextForDetection = computed(() => {
  const attributes = contentAttributes.value || {};
  const emailPayload = attributes.email || {};

  const textContent = emailPayload.textContent || emailPayload.text_content || {};
  const htmlContent = emailPayload.htmlContent || emailPayload.html_content || {};

  const subjectValue = emailPayload.subject || subject.value;

  const replyCandidates = [
    textContent.reply,
    htmlContent.reply,
    textContent.body,
    htmlContent.body,
  ];

  const sanitizedReply = replyCandidates
    .map(sanitizeText)
    .filter(Boolean);

  const sanitizedSubject = sanitizeText(subjectValue);

  const segments = [];

  if (sanitizedReply.length) {
    segments.push(...sanitizedReply);
  }

  if (sanitizedSubject) {
    segments.push(sanitizedSubject);
  }

  if (!segments.length) {
    const fallbackCandidates = [
      textContent.full,
      htmlContent.full,
      attributes.body,
      content?.value,
    ];

    fallbackCandidates
      .map(sanitizeText)
      .filter(Boolean)
      .forEach(value => segments.push(value));
  }

  if (!segments.length) {
    const quotedFallbacks = [
      textContent.quoted,
      htmlContent.quoted,
      emailPayload.body,
      emailPayload.raw_body,
    ];

    quotedFallbacks
      .map(sanitizeText)
      .filter(Boolean)
      .forEach(value => segments.push(value));
  }

  return segments.join(' ').replace(/\s+/g, ' ').trim();
});

const performLanguageDetection = async () => {
  if (!shouldDetectLanguage.value) {
    detectedLanguage.value = null;
    return;
  }

  const text = emailTextForDetection.value;
  if (!text) {
    detectedLanguage.value = null;
    lastDetectedText.value = '';
    return;
  }

  try {
    isDetectingLanguage.value = true;
    console.info('[EmailMeta] language detection input', {
      messageId: id.value,
      conversationId: conversationId.value,
      textSnippet: text.slice(0, 200),
      textLength: text.length,
    });
    const result = await detectLanguage(text);
    console.info('[EmailMeta] language detection raw result', {
      messageId: id.value,
      conversationId: conversationId.value,
      language: result?.language,
      probability: result?.probability,
      label: result?.label,
    });
    detectedLanguage.value = result;
    if (result?.language) {
      const attributes = contentAttributes.value || {};
      if (attributes.email) {
        attributes.email.detected_language = result.language;
        attributes.email.detected_language_label = result.label;
      }
      if (attributes.canned_reply) {
        attributes.canned_reply.detected_language = result.language;
      }
    }
    lastDetectedText.value = text;

    if (result?.language) {
      console.info('[EmailMeta] language detection success', {
        messageId: id.value,
        conversationId: conversationId.value,
        language: result.language,
        probability: result.probability,
        label: result.label,
      });
      emit('language-detected', {
        conversationId: conversationId.value,
        messageId: id.value,
        language: result.language,
        label: result.label,
        probability: result.probability,
      });
    } else {
      console.info('[EmailMeta] language detection returned null', {
        messageId: id.value,
        conversationId: conversationId.value,
      });
    }
  } catch (error) {
    console.error('[EmailMeta] language detection failed', {
      messageId: id.value,
      conversationId: conversationId.value,
      error,
    });
    detectedLanguage.value = null;
  } finally {
    isDetectingLanguage.value = false;
  }
};

const detectOnceOnMount = () => {
  if (detectedLanguage.value) {
    emit('language-detected', {
      conversationId: conversationId.value,
      messageId: id.value,
      language: detectedLanguage.value.language,
      label: detectedLanguage.value.label,
      probability: detectedLanguage.value.probability,
    });
    return;
  }

  if (shouldDetectLanguage.value) {
    performLanguageDetection();
  }
};

onMounted(detectOnceOnMount);

watch(
  [emailTextForDetection, shouldDetectLanguage],
  ([text, allowDetection]) => {
    if (!allowDetection) {
      detectedLanguage.value = null;
      lastDetectedText.value = '';
      return;
    }

    if (text) {
      performLanguageDetection();
    } else {
      detectedLanguage.value = null;
      lastDetectedText.value = '';
    }
  }
);
</script>

<template>
  <section
    v-show="showMeta"
    class="space-y-1 rtl:pl-9 ltr:pr-9 text-sm break-words"
    :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-11'"
  >
    <template v-if="showMeta">
      <div
        v-if="fromEmail[0]"
        :class="hasError ? 'text-n-ruby-11' : 'text-n-slate-12'"
      >
        <template v-if="senderName">
          <span>
            {{ senderName }}
          </span>
          &lt;{{ fromEmail[0] }}&gt;
        </template>
        <template v-else>
          {{ fromEmail[0] }}
        </template>
      </div>
      <div v-if="toEmail.length">
        {{ $t('EMAIL_HEADER.TO') }}: {{ toEmail.join(', ') }}
      </div>
      <div
        v-if="cannedReplyDisplay"
        class="text-xs text-n-slate-11"
        :title="cannedReplyDisplay"
      >
        {{ $t('EMAIL_HEADER.TEMPLATE') }}: {{ cannedReplyDisplay }}
      </div>
      <div v-if="ccEmail.length">
        {{ $t('EMAIL_HEADER.CC') }}:
        {{ ccEmail.join(', ') }}
      </div>
      <div v-if="bccEmail.length">
        {{ $t('EMAIL_HEADER.BCC') }}:
        {{ bccEmail.join(', ') }}
      </div>
      <div v-if="subject" class="text-n-slate-11" :title="subject">
        {{ $t('EMAIL_HEADER.SUBJECT') }}: <span>{{ subject }}</span>
      </div>
      <div
        v-if="detectedLanguageLabel"
        class="text-n-slate-11"
        :title="detectedLanguageLabel"
      >
        {{ $t('EMAIL_HEADER.LANGUAGE_DETECTED', { language: detectedLanguageLabel }) }}
      </div>
    </template>
  </section>
</template>
